/*
 *   File : systematic-plan.js
 *   Author : https://evoqins.com
 *   Description : SWP / STP plan detail modal
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { Icon } from '../Icon';
import { TextButton } from '../Buttons';

import style from '../../Style/Component/tooltip.module.scss';

const MODAL_ID = "systematic-plan-modal";

const displayValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "-") {
        return "-";
    }
    return value;
};

const getStatusTextClass = (status) => {
    const normalized = status?.toLowerCase();

    if (normalized === "active" || normalized === "success" || normalized === "successful" || normalized === "confirmed") {
        return "e-text-green-crayola";
    }
    if (normalized === "failed" || normalized === "cancelled" || normalized === "canceled") {
        return "e-text-maximum-red";
    }
    if (normalized === "pending" || normalized === "under review" || normalized === "under_review") {
        return "e-text-marigold";
    }
    if (normalized === "inactive") return "e-text-taupe-gray";
    return "e-text-raisin-black";
};

const DetailRow = ({ label, labelContent, value, valueClass = "e-text-raisin-black", truncate = false, tooltipId, isLast = false }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const displayText = displayValue(value);
    const showTooltip = truncate && displayText !== "-" && isTruncated;

    useEffect(() => {
        const el = textRef.current;
        if (!el || !truncate || displayText === "-") {
            setIsTruncated(false);
            return;
        }

        const checkTruncation = () => {
            setIsTruncated(el.scrollWidth > el.clientWidth + 1);
        };

        checkTruncation();
        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(el);

        return () => resizeObserver.disconnect();
    }, [displayText, truncate]);

    return (
        <div className={`d-flex justify-content-between align-items-start gap-3  ${isLast ? ' pt-2' : 'border-bottom py-2'}`}>
            <p className='mb-0 e-text-dove-gray e-primary-font e-font-14 e-font-weight-400 line-height-24px flex-shrink-0'>
                {labelContent || label}
            </p>
            {truncate ?
                <div className={`d-flex justify-content-end flex-grow-1 e-primary-font e-font-14 e-font-weight-600 line-height-24px ${valueClass}`}
                    style={{ minWidth: 0 }}>
                    <p ref={textRef}
                        className='mb-0 text-truncate'
                        style={{ maxWidth: '100%', width: 'fit-content' }}
                        data-tooltip-id={showTooltip ? tooltipId : undefined}
                        data-tooltip-content={showTooltip ? displayText : undefined}>
                        {displayText}
                    </p>
                    {showTooltip &&
                        <Tooltip id={tooltipId}
                            place="bottom-end"
                            offset={4}
                            positionStrategy="fixed"
                            className={style.e_custom_tooltip}
                            style={{
                                width: 'auto',
                                maxWidth: '320px',
                                padding: '0.5rem 0.75rem',
                            }} />
                    }
                </div>
                :
                <p className={`mb-0 text-end text-break flex-grow-1 e-primary-font e-font-14 e-font-weight-600 line-height-24px ${valueClass}`}
                    style={{ minWidth: 0 }}>
                    {displayText}
                </p>
            }
        </div>
    );
};

const PlanCard = ({ title, rows }) => (
    <div className='border-radius-8 p-3' style={{ backgroundColor: "#F5F3FF" }}>
        <p className='mb-2 e-text-dark-gray e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
            {title}
        </p>

        {rows.map((row, index) => (
            <DetailRow key={row.label}
                label={row.label}
                labelContent={row.labelContent}
                value={row.value}
                valueClass={row.valueClass}
                truncate={row.truncate}
                tooltipId={row.tooltipId}
                isLast={index === rows.length - 1} />
        ))}
    </div>
);

const SystematicPlanModal = ({ data = {}, handleClose = () => { }, handleViewOrderHistory = null }) => {
    const historyPushedRef = useRef(false);
    const ignorePopStateRef = useRef(false);

    const isStp = data.stp_id != null || data.from_scheme != null || data.rayi_stp_status != null;

    const rayiStatus = isStp ? data.rayi_stp_status : data.rayi_swp_status;
    const fpStatus = isStp ? data.fp_stp_status : data.fp_swp_status;
    const isFailed = rayiStatus?.toLowerCase() === "failed" || fpStatus?.toLowerCase() === "failed";

    const planRows = isStp ?
        [
            { label: "From Scheme", value: data.from_scheme || data.scheme, truncate: true, tooltipId: "stp-from-scheme" },
            { label: "To Scheme", value: data.to_scheme, truncate: true, tooltipId: "stp-to-scheme" },
            { label: "Registered date", value: data.registered_date },
            { label: "STP amount", value: data.stp_amount_text },
            { label: "Start date", value: data.start_date },
            { label: "End date", value: data.end_date },
            { label: "STP day", value: data.stp_day },
            { label: "STP tenure", value: data.stp_tenure },
            { label: "Upcoming transfer", value: data.upcoming_stp_date },
            { label: "Last transfer", value: data.last_stp_date },
            { label: "Executed / Total STPs", value: data.installment_text_with_frequency || data.installment_text },
            { label: "Remaining STPs", value: data.remaining_stp },
            { label: "Rayi STP status", value: rayiStatus, valueClass: getStatusTextClass(rayiStatus) },
        ]
        :
        [
            { label: "Scheme", value: data.scheme, truncate: true, tooltipId: "swp-scheme" },
            { label: "SWP amount", value: data.swp_amount_text },
            { label: "Registered date", value: data.registered_date },
            { label: "SWP day", value: data.swp_day },
            { label: "SWP tenure", value: data.swp_tenure },
            { label: "Upcoming withdrawal", value: data.upcoming_swp_date },
            { label: "Last withdrawal", value: data.last_swp_date },
            { label: "Bank name (Available in Folio)",
                labelContent: <>Bank name <span style={{ color: '#2E8EE6', fontSize: '12px' }}>(Available in Folio)</span></>,
                value: data.bank_name, truncate: true, tooltipId: "swp-bank-name" },
            { label: "Executed / Total SWPs", value: data.installment_text_with_frequency || data.installment_text },
            { label: "Remaining SWPs", value: data.remaining_swp },
            { label: "Rayi SWP status", value: rayiStatus, valueClass: getStatusTextClass(rayiStatus) },
        ];

    const fpRows = [
        {
            label: "FP Plan ID",
            value: data.fp_plan_id || (isStp ? data.fp_stp_id : data.fp_swp_id),
            truncate: true,
            tooltipId: "plan-fp-id",
        },
        {
            label: isStp ? "FP STP status" : "FP SWP status",
            value: fpStatus,
            valueClass: getStatusTextClass(fpStatus),
        },
    ];

    const cancellationRows = [
        { label: isStp ? "Cancelled date and time" : "Cancelled date", value: data.cancelled_date },
        { label: "Cancellation reason", value: data.cancelled_reason, truncate: true, tooltipId: "plan-cancel-reason" },
        { label: "Cancelled by", value: data.cancelled_by },
    ];

    const _dismissModal = () => {
        document.getElementById(`${MODAL_ID}-close`)?.click();
        document.querySelector(".modal-backdrop")?.remove();
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "unset";
        handleClose();
    };

    const _handleCloseModal = () => {
        if (historyPushedRef.current) {
            ignorePopStateRef.current = true;
            historyPushedRef.current = false;
            _dismissModal();
            window.history.back();
            return;
        }
        _dismissModal();
    };

    const _handleOrderHistory = () => {
        if (!handleViewOrderHistory) return;
        historyPushedRef.current = false;
        ignorePopStateRef.current = true;
        _dismissModal();
        handleViewOrderHistory(data);
    };

    useEffect(() => {
        const modalEl = document.getElementById(MODAL_ID);
        if (!modalEl) return;

        const _handleModalShown = () => {
            if (!historyPushedRef.current) {
                window.history.pushState({ systematicPlanModal: true }, "");
                historyPushedRef.current = true;
            }
        };

        const _handlePopState = () => {
            if (ignorePopStateRef.current) {
                ignorePopStateRef.current = false;
                return;
            }

            if (historyPushedRef.current || modalEl.classList.contains("show")) {
                historyPushedRef.current = false;
                _dismissModal();
            }
        };

        modalEl.addEventListener("shown.bs.modal", _handleModalShown);
        window.addEventListener("popstate", _handlePopState);

        return () => {
            modalEl.removeEventListener("shown.bs.modal", _handleModalShown);
            window.removeEventListener("popstate", _handlePopState);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className='modal fade'
            id={MODAL_ID}
            data-bs-backdrop='static'
            data-bs-keyboard='false'
            tabIndex='-1'
            aria-labelledby='systematicPlanModalLabel'
            aria-hidden='true'>
            <div className='modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable'
                style={{ minWidth: '80vw' }}>
                <span id={`${MODAL_ID}-close`}
                    className='d-none'
                    data-bs-dismiss='modal'>
                </span>

                <div className='modal-content border-0 border-radius-28 p-4'>
                    <div className='modal-header border-0 border-bottom p-0 pb-3 justify-content-between align-items-start'>
                        <div>
                            <h6 id='systematicPlanModalLabel'
                                className='mb-2 e-text-raisin-black e-primary-font e-font-16 e-font-weight-600 line-height-24px'>
                                {displayValue(data.customer_name)}
                            </h6>
                            <p className='mb-0 e-text-davys-grey-light e-primary-font e-font-14 e-font-weight-400 line-height-20px'>
                                {displayValue(data.customer_name)} ({displayValue(data.rayi_customer_id)}) | Registered date {displayValue(data.registered_date)}
                                {data.registered_time ? ` | ${data.registered_time}` : ""}
                            </p>
                        </div>

                        <Icon icon='close'
                            size='1.125rem'
                            className='d-flex cursor-pointer'
                            onClick={_handleCloseModal} />
                    </div>

                    <div className='modal-body px-0 pt-3 pb-2' style={{ overflowX: "hidden" }}>
                        <div className='row g-3 mx-0'>
                            <div className='col-12 col-lg-6'>
                                <PlanCard title={isStp ? "STP Details" : "SWP Details"} rows={planRows} />
                            </div>
                            <div className='col-12 col-lg-6 d-flex flex-column gap-3'>
                                <PlanCard title='FP Details' rows={fpRows} />
                                <PlanCard title='Cancellation Details' rows={cancellationRows} />

                                {handleViewOrderHistory &&
                                    <div className='d-flex justify-content-center'>
                                        <TextButton name="View Order History"
                                            fontSize="e-font-14"
                                            fontWeight="e-font-weight-700"
                                            className="text-decoration-underline"
                                            onClick={_handleOrderHistory} />
                                    </div>
                                }
                            </div>
                        </div>

                        {isFailed &&
                            <div className='p-2 mt-3'
                                style={{
                                    backgroundColor: "#F8C0C1",
                                    border: "1px solid #772D2C",
                                    borderRadius: "8px",
                                    color: "#772D2C",
                                }}>
                                <p className='mb-1 e-primary-font e-font-12 e-font-weight-600 line-height-16px'>
                                    Failed reason
                                </p>
                                <p className='mb-0 e-primary-font e-font-12 e-font-weight-400 line-height-16px'>
                                    {displayValue(data.failure_reason)}
                                </p>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystematicPlanModal;
